import { useMemo } from 'react'

import { useModals, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import type { PageContextPayload } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import handleCallbackAction, { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditAgencyProviderPriceOverrideModal from '@modals/CreateOrEditAgencyProviderPriceOverrideModal'

import type { AgencyModel, AgencyProviderPriceOverrideModel, ProviderAccountModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditAgencyProviderPriceOverrideModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditAgencyProviderPriceOverrideModal: {
      closeModal: () => setState({ showCreateOrEditAgencyProviderPriceOverrideModal: false }),
      createAgencyProviderPriceOverride: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAgencyProviderPriceOverride: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

interface ProviderAccountProps {
  agency: AgencyModel,
  providerAccount: ProviderAccountModel,
}

const ProviderAccount = (props: ProviderAccountProps) => {
  const { agency, providerAccount } = props

  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditAgencyProviderPriceOverrideModal,
  } = state

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showCreateOrEditAgencyProviderPriceOverrideModal:
      (payload: { agency_provider_price_override: AgencyProviderPriceOverrideModel }) => {
        setModalData('CreateOrEditAgencyProviderPriceOverrideModal', payload)
        setState({ showCreateOrEditAgencyProviderPriceOverrideModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent agency={agency} providerAccountId={providerAccount?.id} />

        <CreateOrEditAgencyProviderPriceOverrideModal
          callbacks={callbacks('CreateOrEditAgencyProviderPriceOverrideModal', setState)}
          showModal={showCreateOrEditAgencyProviderPriceOverrideModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default ProviderAccount
